/**
 * @module components/QrCode
 * @description QR code drawn as one SVG path.
 *
 * External integration: `qrcode` computes the module matrix synchronously
 * (QRCode.create), so the code is in the DOM on first render — no async
 * image to wait for before printing — and it stays vector-sharp at any size.
 */
import { useMemo } from "react";
import QRCode from "qrcode";

export interface QrCodeProps {
    value: string;
    className?: string;
    /** Accessible description, e.g. "QR code: give to Jamaica". */
    label: string;
}

/** Quiet zone in modules, per the QR specification. */
const QUIET = 4;

export function QrCode({ value, className, label }: QrCodeProps) {
    const { size, path } = useMemo(() => {
        const { modules } = QRCode.create(value, { errorCorrectionLevel: "M" });
        let d = "";
        for (let row = 0; row < modules.size; row++) {
            for (let col = 0; col < modules.size; col++) {
                if (modules.get(row, col)) d += `M${col + QUIET} ${row + QUIET}h1v1h-1z`;
            }
        }
        return { size: modules.size + QUIET * 2, path: d };
    }, [value]);

    return (
        <svg className={className} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label} shapeRendering="crispEdges">
            <rect width={size} height={size} fill="#fff" />
            <path d={path} fill="var(--c-forest)" />
        </svg>
    );
}
